/**
 * Carga e incrusta los recursos de Planer antes de mostrar el documento en un marco
 * srcdoc aislado. El panel puede pedirlos al servidor; el documento con origen opaco no
 * puede depender de esas peticiones.
 *
 * Reutiliza la caché de estilos, puente y gráficas. Para los iconos habituales incluye
 * comunes.css con la fuente reducida; los restantes reciben sus reglas y la fuente
 * completa por mensaje. Véase SUBSET_FONT_URL en shared/icons.ts.
 */
import {
  BRIDGE_PATH,
  CHARTS_PATH,
  CUSTOM_CSS_PATH,
  CUSTOM_JS_PATH,
  STYLES_PATH,
} from "@shared/htmlContract";
import { ICON_NAME_SET } from "@shared/iconNames";
import { FULL_FONT_FILE, ICON_FONT_URL, SUBSET_FONT_URL, SUBSET_ICONS } from "@shared/icons";

/** Cada archivo se pide una vez por sesión del panel. */
const files = new Map<string, Promise<string>>();

/** Descarga un archivo de texto una sola vez y guarda el resultado. */
function file(path: string): Promise<string> {
  const cached = files.get(path);
  if (cached) return cached;
  const pending = fetch(path).then(async (res) => {
    if (!res.ok) {
      // No se usa el cuerpo del error; cancelarlo libera la descarga.
      await res.body?.cancel();
      throw new Error(`No se pudo cargar ${path}`);
    }
    return res.text();
  });
  // Un fallo no se guarda: la próxima página lo vuelve a intentar.
  pending.catch(() => files.delete(path));
  files.set(path, pending);
  return pending;
}

/**
 * Olvida el CSS y el JavaScript comunes ya pedidos: al guardarlos, la próxima
 * página los vuelve a pedir en vez de pegar los de antes.
 */
export function forgetCustomCode(): void {
  files.delete(CUSTOM_CSS_PATH);
  files.delete(CUSTOM_JS_PATH);
}

/*
 * LOS ICONOS FUERA DEL SUBCONJUNTO
 *
 * El subconjunto pegado dibuja los nombres más comunes al instante. Una página
 * puede usar cualquiera de los 6228: para esos se pegan solo sus reglas
 * --unas líneas por icono, leídas de la hoja completa-- y la fuente entera
 * llega aparte, en binario y por mensaje, porque el marco no puede pedirla. Se
 * registra con otro nombre de familia y va primero: mientras llega, lo que el
 * subconjunto sabe dibujar ya se ve.
 */
const FULL_FAMILY = "hugeicons-completa";
const SUBSET_SET = new Set(SUBSET_ICONS);

/** Nombre → punto de código, leído una vez de la hoja completa. */
let glyphs: Promise<Map<string, string>> | null = null;
/** Mapa de nombre de icono a su carácter en la fuente, leído de la hoja de iconos. */
function glyphMap(): Promise<Map<string, string>> {
  glyphs ??= file(ICON_FONT_URL).then((css) => {
    const map = new Map<string, string>();
    for (const match of css.matchAll(
      /\.icon\.icon-([a-z0-9-]+)::before\s*\{\s*content:\s*"\\([0-9a-f]+)"/g,
    ))
      map.set(match[1], match[2]);
    return map;
  });
  glyphs.catch(() => (glyphs = null));
  return glyphs;
}

/**
 * Los iconos que la página nombra y el subconjunto no trae: los que van como
 * clase (`icon-nombre`) y los que van como texto entre comillas --un mapa de
 * estado a icono, por ejemplo--. Un nombre armado a trozos no se ve, y por eso
 * el contrato pide escribirlos enteros.
 */
function extraIcons(html: string): string[] {
  const found = new Set<string>();
  for (const match of html.matchAll(/icon-([a-z0-9-]+)/g)) found.add(match[1]);
  for (const match of html.matchAll(/["'`]([a-z0-9-]+)["'`]/g)) found.add(match[1]);
  return [...found].filter((name) => ICON_NAME_SET.has(name) && !SUBSET_SET.has(name));
}

/** La fuente entera, en binario, pedida una sola vez. */
let fullFont: Promise<ArrayBuffer> | null = null;
/**
 * Bytes de la fuente completa de iconos, que se entrega al marco de la página cuando usa un icono
 * fuera del subconjunto.
 */
function fontBytes(): Promise<ArrayBuffer> {
  fullFont ??= fetch(FULL_FONT_FILE).then(async (res) => {
    if (!res.ok) {
      // Un recurso fallido tampoco debe dejar un cuerpo sin lector.
      await res.body?.cancel();
      throw new Error(`No se pudo cargar ${FULL_FONT_FILE}`);
    }
    return res.arrayBuffer();
  });
  fullFont.catch(() => (fullFont = null));
  return fullFont;
}

/*
 * Las letras de la casa --Inter y Reddit Mono--, las mismas del panel.
 *
 * El marco no puede pedirlas: su origen es nulo y `/fuentes/` le queda tan
 * lejos como le quedaba la fuente de iconos. Sin esto una página se veía con
 * la letra del sistema aunque su hoja dijera Inter, y el panel y lo publicado
 * no parecían la misma casa. Llegan igual que la fuente de iconos entera: el
 * marco las pide por mensaje y las registra con `FontFace`.
 */
const LETTERS = [
  { familia: "Inter", peso: "100 900", archivo: "/fuentes/inter.woff2" },
  { familia: "Reddit Mono", peso: "200 900", archivo: "/fuentes/reddit-mono.woff2" },
] as const;

type Letter = { familia: string; peso: string; fuente: ArrayBuffer };

/** Las letras en binario, pedidas una sola vez. */
let letters: Promise<Letter[]> | null = null;
/** Bytes de las tipografías de la interfaz, que se entregan al marco de la página. */
function letterBytes(): Promise<Letter[]> {
  letters ??= Promise.all(
    LETTERS.map(async ({ familia, peso, archivo }) => {
      const res = await fetch(archivo);
      if (!res.ok) {
        // La respuesta se descarta, incluida su descarga pendiente.
        await res.body?.cancel();
        throw new Error(`No se pudo cargar ${archivo}`);
      }
      return { familia, peso, fuente: await res.arrayBuffer() };
    }),
  );
  letters.catch(() => (letters = null));
  return letters;
}

/*
 * Un solo oyente para todos los marcos: la previsualización, la sonda de la
 * IA y la conversión pasan por aquí, así que cualquiera que pida una fuente la
 * recibe. Son públicas; no hay nada que proteger en quién las pide.
 */
let listening = false;
/**
 * Atiende los mensajes del marco de una página que pide fuentes, ya que el marco no puede
 * descargarlas por sí mismo.
 */
function serveFonts(): void {
  if (listening) return;
  listening = true;
  window.addEventListener("message", (event) => {
    const source = event.source as Window | null;
    if (!source) return;
    if (event.data?.plane === "iconos-fuente") {
      void fontBytes()
        .then((fuente) => source.postMessage({ plane: "iconos-fuente", fuente }, "*"))
        .catch(() => {});
    } else if (event.data?.plane === "letras") {
      void letterBytes()
        .then((letras) => source.postMessage({ plane: "letras", letras }, "*"))
        .catch(() => {});
    }
  });
}

/** Las reglas de los iconos extra y lo que pide la fuente desde el marco. */
async function extraIconsCss(html: string): Promise<string> {
  const names = extraIcons(html);
  if (!names.length) return "";
  const map = await glyphMap().catch(() => new Map<string, string>());
  const rules = names
    .filter((name) => map.has(name))
    .map((name) => `.icon.icon-${name}::before{content:"\\${map.get(name)}"}`);
  if (!rules.length) return "";
  serveFonts();
  return `.icon{font-family:"${FULL_FAMILY}","hugeicons-stroke-rounded"!important}${rules.join("")}`;
}

/** Lo que corre dentro del marco: pide la fuente y la registra al llegar. */
const FONT_REQUEST = `(function(){addEventListener("message",function(e){var d=e.data;if(!d||d.plane!=="iconos-fuente"||!d.fuente||e.source!==parent)return;new FontFace("${FULL_FAMILY}",d.fuente).load().then(function(f){document.fonts.add(f)}).catch(function(){})});parent.postMessage({plane:"iconos-fuente"},"*")})()`;

/** Lo que corre dentro del marco: pide las letras de la casa y las registra. */
const LETTER_REQUEST = `(function(){addEventListener("message",function(e){var d=e.data;if(!d||d.plane!=="letras"||!d.letras||e.source!==parent)return;d.letras.forEach(function(l){new FontFace(l.familia,l.fuente,{weight:l.peso,display:"swap"}).load().then(function(f){document.fonts.add(f)}).catch(function(){})})});parent.postMessage({plane:"letras"},"*")})()`;

/**
 * La referencia, tal como la escribió `server/page/pageAssets.ts` o la propia
 * página. Se reconoce por la ruta, no por la línea entera: el documento pudo
 * escribirla con la dirección absoluta, con comillas simples o con atributos
 * de más.
 */
function refPattern(path: string, tag: "link" | "script"): RegExp {
  const route = path.replace(/[/.]/g, "\\$&");
  return tag === "link"
    ? new RegExp(`<link\\b[^>]*href=["'][^"']*${route}["'][^>]*>`, "gi")
    : new RegExp(`<script\\b[^>]*src=["'][^"']*${route}["'][^>]*>\\s*</script>`, "gi");
}

/**
 * Un `</script` dentro del código cerraria la etiqueta que lo envuelve. El
 * parser mira el texto crudo, así que hay que partirlo; para el motor de
 * JavaScript la barra escapada no cambia nada.
 */
function safeScript(code: string): string {
  return code.replace(/<\/script/gi, "<\\/script");
}

/**
 * El mismo documento con los archivos de Planer dentro. Lo que no tenga
 * referencia se queda igual: las gráficas solo van donde se dibuja algo.
 */
export async function inlinePlaneAssets(html: string): Promise<string> {
  const styles = refPattern(STYLES_PATH, "link");
  const bridge = refPattern(BRIDGE_PATH, "script");
  const charts = refPattern(CHARTS_PATH, "script");
  const icons = refPattern(ICON_FONT_URL, "link");
  const customCss = refPattern(CUSTOM_CSS_PATH, "link");
  const customJs = refPattern(CUSTOM_JS_PATH, "script");

  const [css, js, chartsJs, iconCss, ownCss, ownJs] = await Promise.all([
    styles.test(html) ? file(STYLES_PATH) : null,
    bridge.test(html) ? file(BRIDGE_PATH) : null,
    charts.test(html) ? file(CHARTS_PATH) : null,
    icons.test(html) ? file(SUBSET_FONT_URL) : null,
    // Sin el código común la página se dibuja igual: un fallo lo deja fuera.
    customCss.test(html) ? file(CUSTOM_CSS_PATH).catch(() => "") : null,
    customJs.test(html) ? file(CUSTOM_JS_PATH).catch(() => "") : null,
  ]);
  // `test` sobre una expresión global deja el cursor donde encontró: hay que
  // devolverlo al principio antes de reemplazar.
  styles.lastIndex = 0;
  bridge.lastIndex = 0;
  charts.lastIndex = 0;
  icons.lastIndex = 0;
  customCss.lastIndex = 0;
  customJs.lastIndex = 0;

  /* El reemplazo va como función a propósito: un `$&` o un `$'` sueltos en el
     código del archivo cambiarian de significado dentro de un texto de
     reemplazo, y aquí lo que entra tiene que salir tal cual. */
  let out = html;
  /* Los iconos, antes que los estilos: la hoja de la casa da por puesta la
     familia de la fuente, y así el orden del documento dice el mismo orden en
     que hacen falta. */
  if (iconCss) {
    const extra = await extraIconsCss(html);
    const request = extra ? `<script data-plane="iconos-completos">${FONT_REQUEST}</script>` : "";
    out = out.replace(
      icons,
      () => `<style data-plane="iconos">${iconCss}${extra}</style>${request}`,
    );
  }
  /* Con la hoja de la casa van sus letras: la hoja dice Inter, y sin ellas el
     marco caería en la del sistema. */
  if (css) {
    serveFonts();
    out = out.replace(
      styles,
      () =>
        `<style data-plane="estilos">${css}</style><script data-plane="letras">${LETTER_REQUEST}</script>`,
    );
  }
  if (js) out = out.replace(bridge, () => `<script data-plane="puente">${safeScript(js)}</script>`);
  if (chartsJs)
    out = out.replace(
      charts,
      () => `<script data-plane="graficas">${safeScript(chartsJs)}</script>`,
    );
  if (ownCss !== null)
    out = out.replace(
      customCss, // Un `</style` dentro del CSS cerraría la etiqueta antes de tiempo.
      () =>
        `<style data-plane="personalizado">${ownCss.replace(/<\/style/gi, "<\\/style")}</style>`,
    );
  if (ownJs !== null)
    out = out.replace(
      customJs,
      () => `<script data-plane="personalizado">${safeScript(ownJs)}</script>`,
    );
  return out;
}
